import { useMemo } from "react"

import useCapacities from "@/queries/hooks/useCapacities"
import type { Product } from "@/queries/models"
import type { SelectedProduct } from "@/types/Product"

import {
  getBlockedStatus,
  getCapacityStatus,
  isAllowedByCart,
  isAllowedBySession,
} from "./helpers"

export type ProductStatus = "limited" | "low-stock" | "out-of-stock" | "blocked"

export type ProductStatusInfo = {
  status: ProductStatus
  statusDescription: string
  quantityOnHand?: number
}

type UseProductStatusParams = {
  products: Product[]
  capacityGroupIds: string[]
  selectedProducts: SelectedProduct[]
  isSeasonProductInCart?: boolean
  hasServiceProductInCart?: boolean
}

type UseProductStatusReturn = {
  getProductStatus: (productId: string) => ProductStatusInfo | undefined
  isProductSelectable: (productId: string) => boolean
  hasBlockedProducts: boolean
  isLoading: boolean
}

// Products are evaluated in priority order:
// 1. Selectability rules (cart/session restrictions) — blocks products entirely
// 2. Capacity status — only checked for selectable products, avoiding unnecessary API queries
const useProductStatus = ({
  products,
  capacityGroupIds,
  selectedProducts,
  isSeasonProductInCart = false,
  hasServiceProductInCart = false,
}: UseProductStatusParams): UseProductStatusReturn => {
  const hasExistingCartItems = isSeasonProductInCart || hasServiceProductInCart
  // Step A: Build selectability map — null means no restrictions apply
  const selectabilityMap = useMemo(() => {
    if (
      !products?.length ||
      (!hasExistingCartItems && selectedProducts.length === 0)
    ) {
      return null
    }

    const hasSelectedSubscriptions = selectedProducts.some(
      (p) => p.isSubscriptionProduct
    )

    const isSelectable = (product: Product): boolean =>
      hasExistingCartItems
        ? isAllowedByCart(product, isSeasonProductInCart)
        : isAllowedBySession(product, hasSelectedSubscriptions)

    return new Map(
      products.map((product) => [product.id, isSelectable(product)])
    )
  }, [products, selectedProducts, isSeasonProductInCart, hasExistingCartItems])

  const isProductSelectable = (productId: string): boolean =>
    selectabilityMap?.get(productId) ?? true

  const selectableProducts = selectabilityMap
    ? products.filter((p) => selectabilityMap.get(p.id))
    : products

  const hasBlockedProducts =
    hasExistingCartItems && products.some((p) => !isProductSelectable(p.id))

  // Step B: Fetch capacity data only for selectable products
  const selectableCapacityGroupIds = capacityGroupIds.filter((id) =>
    selectableProducts.some((product) => product.capacityGroupId === id)
  )

  const { data: capacities, isLoading: isCapacitiesLoading } = useCapacities({
    capacityGroupIds: selectableCapacityGroupIds,
  })

  // Step C: Build status map — blocked by rules first, then capacity
  const statusMap = useMemo(() => {
    const map = new Map<string, ProductStatusInfo>()

    products?.forEach((product) => {
      const isSelectable = selectabilityMap?.get(product.id) ?? true

      const status =
        getBlockedStatus(isSelectable) ||
        getCapacityStatus(product.capacityGroupId, capacities)

      if (status) {
        map.set(product.id, status)
      }
    })

    return map
  }, [products, capacities, selectabilityMap])

  const getProductStatus = (
    productId: string
  ): ProductStatusInfo | undefined => {
    return statusMap.get(productId)
  }

  return {
    getProductStatus,
    isProductSelectable,
    hasBlockedProducts,
    isLoading: isCapacitiesLoading,
  }
}

export default useProductStatus
